import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getProductListAsync } from "src/api/product/product-api";
import { ProductListParams, ProductModel } from "src/api/product/product-types";
import { BaseButton, BaseTable } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useQueryParams } from "src/hooks/useQueryParams";
import { axiosInstance, BULLET } from "src/utils";
import { ProductColumns } from "../../product/columns";
import { Order, UnionCmdType } from "src/api/public-types";
import { useLocation, useParams } from "react-router-dom";
import SearchSpaceProductPopup from "src/pages/commonPopup/SearchSpaceProductPopup";
import { useRecoilValue } from "recoil";
import { globalPartnerState } from "src/recoil/partners/atom";
import GoToListButton from "src/components/GoToListButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { useModal } from "src/recoil/modalState/hook";
import { Cell } from "react-table";
import _ from "lodash";
import { getGuideListAsync, getGuideServiceAssociationsAsync, postGuideMappingAsync } from "src/api/guide/guide-api";
import { ServiceUserGuide } from "src/api/guide/guide-types";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { PagePath } from "src/pages/product/details";
import BaseNewTabLink from "src/components/BaseNewTabLink";

// 헤더명(상품 ID)과 상품명(상세 이동 미제공)은 상품 목록 컬럼을 그대로 사용하고, 상품 ID 링크만 새 탭으로 연다
const overrideColumns = [
  {
    accessor: "id",
    Cell: (props: Cell<ProductModel>) => {
      const detailPath = `${PagePath.product.detail.replace(":id", `${props.row.original.id}`)}`;

      return (
        <div>
          <BaseNewTabLink path={detailPath} value={props.value} ignoreQueryParams tooltip />
        </div>
      );
    },
  },
];

/**
 * 이용안내 > 상품 목록 화면
 */
const GuideProductList = () => {
  const {
    queryParams: { id },
  } = useQueryParams();
  const { isAuthority } = usePartnerAuthority();
  const location = useLocation();
  const { setBaseModal } = useModal();
  const ServiceType = "SERVICE_PRODUCT";

  // 파트너 정보
  const partner = useRecoilValue(globalPartnerState);

  // 모달 오픈
  const [isOpenProductPopup, setIsOpenProductPopup] = useState(false);

  // 연관 상품 조회
  const { data: guideServiceAssociationsData, refetch: refetchGuideServiceAssociations, isFetched: isFetchedGuideServiceAssociations } = useQuery({
    enabled: !!id,
    queryKey: ["getGuideServiceAssociationsAsync", id, ServiceType],
    queryFn: () => getGuideServiceAssociationsAsync(axiosInstance, { pathVariable: { id: Number(id) }, queryParam: { serviceTypes: ServiceType } }),
    select: (res) => res.data.data.content,
  });

  // 이용안내 상세 정보
  const { data: guideDetailData } = useQuery({
    enabled: !!id,
    queryKey: ["getUserGuideDetailAsync", id],
    queryFn: () => getGuideListAsync(axiosInstance, { page: 0, size: 1, idList: id }),
    select: (res) => res?.data?.data,
  });

  // provider Id
  const providerId = useMemo(() => guideDetailData?.content?.[0]?.providerId, [guideDetailData]);

  // 이용안내 제목
  const subject = useMemo(() => guideDetailData?.content?.[0]?.subject || "", [guideDetailData]);

  const selectedProductIds = useMemo(() => {
    return guideServiceAssociationsData?.map((association) => association.serviceId) || [];
  }, [guideServiceAssociationsData]);

  // URL 쿼리 파라미터 관리
  const { queryParams, queryParamsNavigate } = useQueryParams<ProductListParams>({
    page: 0,
    size: 20,
    sort: { orders: [{ property: "id", direction: "DESC" }] },
  });

  // id, tab, list 쿼리 파라미터 제외, productIds 추가
  const filteredQueryParams = useMemo(() => {
    const { id, tab, list, ...rest } = (queryParams as { [key: string]: any }) || {};

    const result = { ...rest };
    const productIdsJoin = selectedProductIds?.join(",");
    if (productIdsJoin) {
      result.id = productIdsJoin;
    }
    return result;
  }, [queryParams, selectedProductIds]);

  // 검색 필터 변경 시 URL 업데이트
  const updateSearchFilter = (updates: Partial<ProductListParams>) => {
    queryParamsNavigate({ ...queryParams, ...updates, page: 0 });
  };

  // 상품 목록 조회
  const { data: productListData, isFetched: isFetchedProductListData } = useQuery({
    enabled: !_.isEmpty(selectedProductIds),
    queryKey: ["getProductListAsync", filteredQueryParams],
    queryFn: () => getProductListAsync(axiosInstance, filteredQueryParams),
    select: (res) => res.data,
    placeholderData: keepPreviousData,
  });

  // 목록 아이템
  const listData = useMemo(() => {
    if (selectedProductIds.length === 0) {
      return [];
    }

    return productListData?.data?.content || [];
  }, [productListData, selectedProductIds]);

  // Total Pages
  const totalPages = useMemo(() => {
    if (selectedProductIds.length === 0) {
      return 0;
    }

    return productListData?.meta?.pageMeta?.totalPages || 0;
  }, [productListData, selectedProductIds]);

  // Total Elements
  const totalElements = useMemo(() => {
    if (selectedProductIds.length === 0) {
      return 0;
    }

    return productListData?.meta?.pageMeta?.totalElements || 0;
  }, [productListData, selectedProductIds]);

  // 연결정보 저장
  const { mutateAsync: mutateAsyncSaveGuideMapping } = useMutation({
    mutationFn: async (param: ServiceUserGuide[]) => await postGuideMappingAsync(axiosInstance, param),
    onSuccess: () => {
      // 연결정보 재조회
      refetchGuideServiceAssociations();
    },
  });

  // 삭제 처리 컬럼
  const deleteColumn = useMemo(() => {
    return {
      Header: "삭제",
      accessor: "deleteButton",
      width: 60,
      Cell: ({ row }: Cell<ProductModel>) => {
        return (
          <button
            className="base-trash-btn color-gray"
            onClick={(e) => {
              e.preventDefault();
              setBaseModal({
                isOpen: true,
                title: "항목을 삭제하시겠습니까?",
                onClick: async () => {
                  console.debug("삭제할 항목", row.original.id);
                  setBaseModal({ isOpen: false });

                  if (!_.isEmpty(guideServiceAssociationsData)) {
                    const userGuideDeleteMappings: ServiceUserGuide[] = (guideServiceAssociationsData || [])
                      .filter((association) => {
                        return String(association.serviceId) === String(row.original.id);
                      })
                      .map((association) => ({
                        id: association.id,
                        serviceId: association.serviceId,
                        serviceType: association.serviceType as UnionServiceType,
                        cmdType: "D" as UnionCmdType,
                      }));

                    await mutateAsyncSaveGuideMapping(userGuideDeleteMappings);

                    updateSearchFilter({});
                  }
                },
                onClose: () => {
                  setBaseModal({ isOpen: false });
                },
                btnLeftTitle: "취소",
                btnRightTitle: "확인",
              });
            }}
          ></button>
        );
      },
      sticky: "right",
    };
  }, [setBaseModal, guideServiceAssociationsData, id, mutateAsyncSaveGuideMapping]);

  // 컬럼 정의
  const tableColumns = useMemo(() => {
    const defaultColumns = ProductColumns.map((column) => {
      const result = _.omit(column, ["sticky"]); // 상품 컬럼 sticky 속성 제거

      const overrideColumn = overrideColumns.find((col) => col.accessor === column.accessor);
      if (overrideColumn) {
        return { ...result, ...overrideColumn };
      }

      return result;
    });

    const cols = [...defaultColumns];

    if (isAuthority("d")) {
      cols.push(deleteColumn);
    }

    return cols;
  }, [deleteColumn, isAuthority]);

  // 테이블 노출 여부
  const visibleTable = useMemo(() => {
    if (isFetchedGuideServiceAssociations && selectedProductIds.length === 0) {
      return true;
    }

    if (isFetchedProductListData) {
      return true;
    }

    return false;
  }, [isFetchedGuideServiceAssociations, isFetchedProductListData, selectedProductIds]);

  return (
    <div className="contents-container__table justify-contents-between">
      <div className="contents-container__table-with-title-and-btn-wrap">
        <div className="contents-container__search-wrap pt30">
          <div className="left-area">
            <BaseSectionTitle title={"연결정보(상품)"} className="pb0" />
          </div>
          {isAuthority("w") && (
            <div className="right-area">
              <BaseButton title="연결 관리" onClick={() => setIsOpenProductPopup(true)} />

              {isOpenProductPopup && (
                <SearchSpaceProductPopup
                  title={"연결정보(상품) 선택"}
                  description={`${BULLET} 설정중인 이용안내 항목명 : ${subject}`}
                  isOpen
                  partnerId={String(partner?.id)}
                  isMultiSelect={true}
                  isValidationCheck={false}
                  visibleProductStatusSelect={true}
                  visibleProductTypeSelect={true}
                  visibleContractAvailableCheckBox={false}
                  existProductIds={selectedProductIds}
                  providerIds={providerId}
                  onClose={() => setIsOpenProductPopup(false)}
                  onClick={(products) => {
                    console.debug("products", products);
                    setIsOpenProductPopup(false);

                    const userGuideMapping: ServiceUserGuide[] = products.map((product) => ({
                      cmdType: "C",
                      userGuideId: String(id),
                      serviceId: String(product.id),
                      serviceType: ServiceType,
                      buildingId: String(product.buildingId),
                      orderNums: 0,
                      isDisplayed: true,
                    }));

                    mutateAsyncSaveGuideMapping(userGuideMapping);
                  }}
                />
              )}
            </div>
          )}
        </div>

        {visibleTable && (
          <BaseTable
            data={listData}
            columns={tableColumns}
            currentSize={Number(queryParams.size) || 20}
            sizeOption={(size) => updateSearchFilter({ size })}
            pageIndex={Number(queryParams?.page || 0)}
            totalPages={totalPages}
            totalElements={totalElements}
            goPage={(page: number) => queryParamsNavigate({ ...queryParams, page })}
            orders={queryParams.sort?.orders}
            setOrders={(orders: Order[]) => orders && updateSearchFilter({ sort: { orders } })}
            disabledSortHeaders={["rentalCost", "deleteButton"]}
          />
        )}
      </div>
      <div className="contents-container__btn-wrap">
        <div className="left-area d-flex">
          <GoToListButton />
        </div>
      </div>
    </div>
  );
};

export default GuideProductList;
